import { Button } from "@patient-watch/ui/button";
import { selectTriggerVariants } from "@patient-watch/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@patient-watch/ui/command";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@patient-watch/ui/popover";
import { cn } from "@patient-watch/ui";
import { CheckIcon, ChevronsUpDown, Lock } from "lucide-react";

import type { OptionT } from "./Select";
import type { LooseRHFField } from "./utils/loose-rhf-field";
import { reactNodeToPlainString } from "./utils/reactNodeToPlainString";

interface Props {
    label: React.ReactNode;
    options: OptionT[];
    form: any;
    disabled: boolean;
    description?: string;
    attribute: string;
    placeholder: string;
}

export function ProfileCombobox({ label, attribute, description, placeholder, disabled, options, form }: Props) {
    if (options?.length === 0) {
        return <p>No Options</p>;
    }

    return (
        <FormField
            control={form.control}
            name={attribute}
            render={({ field }: { field: LooseRHFField }) => (
                <FormItem className="flex flex-col">
                    <FormLabel>{label}</FormLabel>
                    <Popover>
                        <PopoverTrigger disabled={disabled} asChild>
                            <FormControl>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    role="combobox"
                                    className={cn(
                                        selectTriggerVariants({ size: "md" }),
                                        "justify-between pl-3 font-normal shadow-none hover:bg-white dark:hover:bg-slate-900",
                                        !field.value && "text-slate-500"
                                    )}
                                >
                                    {field.value ? options.find((o) => o.value === field.value)?.label : placeholder}
                                    {disabled ? (
                                        <Lock className="opacity-50 ml-2 w-4 h-4 shrink-0" />
                                    ) : (
                                        <ChevronsUpDown className="opacity-50 ml-2 w-4 h-4 shrink-0" />
                                    )}
                                </Button>
                            </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-full">
                            <Command>
                                <CommandInput placeholder={placeholder} className="h-9" />
                                <CommandEmpty>No results found.</CommandEmpty>
                                <CommandGroup>
                                    {options.map((o) => (
                                        <CommandItem
                                            value={reactNodeToPlainString(o.label) || o.value}
                                            key={o.value}
                                            onSelect={() => {
                                                form.setValue(`${attribute}`, o.value);
                                            }}
                                        >
                                            {o.label}
                                            <CheckIcon
                                                className={cn(
                                                    "ml-auto w-4 h-4",
                                                    o.value === field.value ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </Command>
                        </PopoverContent>
                    </Popover>
                    {description ?? <FormDescription>{description}</FormDescription>}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}

export default ProfileCombobox;
